import { FieldConfig, useField } from 'formik';
import { debounce } from 'lodash-es';
import { ComponentProps, useEffect, useRef } from 'react';

import useFormikContext from '../../../hooks/useFormikContext';
import { isNullable } from '../../../utils/is';
import { formatKoreanByConsonant } from '../../../utils/string';
import FormControl from '../FormControl';
import TextInput, { TextInputProps } from '../TextInput';

type Props = {
  label: string;
  labelConfig?: ComponentProps<typeof FormControl.Label>;
  caption?: string;
  debounce?: boolean;
} & Omit<TextInputProps, 'type'> &
  Pick<FieldConfig, 'validate'>;

const TextField = ({
  label,
  labelConfig,
  disabled: propDisabled,
  required,
  name = '',
  onChange: propOnChange,
  value: propValue,
  caption,
  debounce: propDebounce = false,
  validate,
  ...props
}: Props) => {
  const inputId = `text_field_for_${label}`;

  const [{ value: baseValue, onChange, ...restFieldProps }, { error }, { setError }] = useField({
    name,
    validate: (value) => {
      const errorMessage = validate?.(value);
      if (errorMessage) return errorMessage;
      if (required && (isNullable(value) || value === '')) {
        return `${formatKoreanByConsonant(label, '을', '를')} 입력해 주세요`;
      }
    },
  });

  const value = isNullable(propValue) ? baseValue : propValue;

  const { isSubmitting } = useFormikContext();

  const handleChange: TextInputProps['onChange'] = (...arg) => {
    setError(undefined);

    if (propOnChange) propOnChange(...arg);
    else onChange(...arg);
  };

  const textInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (textInputRef.current) {
      textInputRef.current.value = value || '';
    }
  }, [value]);

  const disabled = propDisabled || isSubmitting;

  return (
    <FormControl disabled={disabled} required={required}>
      <FormControl.Label {...labelConfig} htmlFor={inputId}>
        {label}
      </FormControl.Label>
      <TextInput
        {...restFieldProps}
        ref={textInputRef}
        id={inputId}
        block
        type={'text'}
        disabled={disabled}
        onChange={propDebounce ? debounce(handleChange, 500) : handleChange}
        {...(error ? { validationStatus: 'error' } : {})}
        {...props}
      />
      {error ? (
        <FormControl.Validation variant={'error'}>{error}</FormControl.Validation>
      ) : caption ? (
        <FormControl.Caption sx={{ whiteSpace: 'pre-wrap' }}>{caption}</FormControl.Caption>
      ) : null}
    </FormControl>
  );
};

export default Object.assign(TextField, { Action: TextInput.Action, DialogAction: TextInput.DialogAction });
export type { Props as TextFieldProps };
